'use client'

/**
 * Custom hook for in-app notifications with polling.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  notificationsApi,
  type Notification,
  type NotificationListParams,
} from '@/lib/notifications-api'

interface UseNotificationsState {
  notifications: Notification[]
  unreadCount: number
  total: number
  isLoading: boolean
  error: string | null
}

const POLL_INTERVAL_MS = 30_000

export function useNotifications(params: NotificationListParams = {}) {
  const [state, setState] = useState<UseNotificationsState>({
    notifications: [],
    unreadCount: 0,
    total: 0,
    isLoading: true,
    error: null,
  })

  const paramsKey = JSON.stringify(params)

  const fetchNotifications = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: prev.notifications.length === 0, error: null }))
    try {
      const result = await notificationsApi.list(params)
      setState({
        notifications: result.items,
        unreadCount: result.unread_count,
        total: result.total,
        isLoading: false,
        error: null,
      })
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch notifications',
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey])

  const markRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.markRead(notificationId)
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }))
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to mark notification as read',
      }))
    }
  }, [])

  const markAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead()
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0,
      }))
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to mark all as read',
      }))
    }
  }, [])

  useEffect(() => {
    void fetchNotifications()

    const refreshIntervalId = window.setInterval(() => {
      void fetchNotifications()
    }, POLL_INTERVAL_MS)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchNotifications()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.clearInterval(refreshIntervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [fetchNotifications])

  return {
    ...state,
    refetch: fetchNotifications,
    markRead,
    markAllRead,
  }
}
